import { importWithOneRetry } from './dynamicImportRetry';
import {
  analyticsSafeHref,
  analyticsSafePathname,
  isUnsubscribeRoutePathname,
  sanitizePosthogEvent,
  sanitizePosthogProperties,
} from './sanitizeAnalyticsUrl';
import { applyInternalTrafficFlag } from './internalTraffic';
import {
  buildPosthogBatchPayload,
  isPosthogCapturingOptedOut,
  sendPosthogBatch,
  type BeaconQueueItem,
  type BeaconTransportResult,
  type PosthogBatchPayload,
} from './posthogEarlyExitBeacon';

type PostHogJsClient = {
  capture: (
    event: string,
    properties?: Record<string, unknown>,
    options?: { timestamp?: Date; send_instantly?: boolean },
  ) => void;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  reset: () => void;
  register: (properties: Record<string, unknown>) => void;
  getFeatureFlag: (flag: string) => string | boolean | undefined;
  onFeatureFlags: (callback: () => void) => () => void;
  init?: (key: string, options: Record<string, unknown>) => void;
  set_config?: (config: Record<string, unknown>) => void;
};

const MAX_QUEUE_ITEMS = 500;
/** Captures at or below this count replay with send_instantly (proven /e/ path). */
const REPLAY_SEND_INSTANTLY_MAX_CAPTURES = 25;

function posthogKey(): string | undefined {
  return process.env.REACT_APP_POSTHOG_KEY;
}

function posthogHost(): string {
  return process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
}

type QueueCapture = {
  kind: 'capture';
  event: string;
  properties?: Record<string, unknown>;
  ts: number;
  id: number;
};

type QueueIdentify = {
  kind: 'identify';
  distinctId: string;
  properties?: Record<string, unknown>;
  ts: number;
  id: number;
};

type QueueReset = { kind: 'reset'; ts: number; id: number };

type QueueRegister = {
  kind: 'register';
  properties: Record<string, unknown>;
  ts: number;
  id: number;
};

type QueueItem = QueueCapture | QueueIdentify | QueueReset | QueueRegister;

const queue: QueueItem[] = [];
let nextQueueId = 1;
let posthogClient: PostHogJsClient | null = null;
let initPromise: Promise<PostHogJsClient | null> | null = null;
let replayed = false;
let lastBeaconResult: BeaconTransportResult | null = null;

const readyListeners = new Set<(client: PostHogJsClient | null) => void>();

export function subscribePostHogReady(listener: (client: PostHogJsClient | null) => void): () => void {
  readyListeners.add(listener);
  if (posthogClient && replayed) listener(posthogClient);
  return () => readyListeners.delete(listener);
}

function notifyReady(client: PostHogJsClient | null): void {
  readyListeners.forEach((listener) => listener(client));
}

function captureTimeUrlProps(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  return {
    $current_url: analyticsSafeHref(),
    $pathname: analyticsSafePathname(),
  };
}

function trimQueueIfNeeded(): void {
  while (queue.length > MAX_QUEUE_ITEMS) {
    queue.shift();
  }
}

function pendingQueueItems(): QueueItem[] {
  return queue.slice();
}

function removeQueueItemsById(ids: Set<number>): void {
  for (let i = queue.length - 1; i >= 0; i -= 1) {
    if (ids.has(queue[i].id)) {
      queue.splice(i, 1);
    }
  }
}

function toBeaconItems(items: QueueItem[]): BeaconQueueItem[] {
  return items.map(({ id: _id, ...rest }) => rest as BeaconQueueItem);
}

/** Capture queue ids included in a successful beacon batch (register/identify/reset stay queued). */
function captureIdsIncludedInBeaconBatch(
  pending: QueueItem[],
  payload: PosthogBatchPayload,
): Set<number> {
  const ids = new Set<number>();
  let sentCaptures = 0;
  for (const item of pending) {
    if (item.kind !== 'capture') continue;
    if (sentCaptures >= payload.batch.length) break;
    ids.add(item.id);
    sentCaptures += 1;
  }
  return ids;
}

function replayQueue(client: PostHogJsClient): void {
  const pending = pendingQueueItems();
  if (pending.length === 0) {
    replayed = true;
    return;
  }

  const captureCount = pending.filter((item) => item.kind === 'capture').length;
  let captureIndex = 0;

  for (const item of pending) {
    switch (item.kind) {
      case 'register':
        client.register(item.properties);
        break;
      case 'identify':
        client.identify(item.distinctId, item.properties);
        break;
      case 'reset':
        client.reset();
        break;
      case 'capture':
        captureIndex += 1;
        {
          const useInstant =
            captureCount <= REPLAY_SEND_INSTANTLY_MAX_CAPTURES ||
            captureIndex === captureCount;
          client.capture(item.event, item.properties ?? {}, {
            timestamp: new Date(item.ts),
            send_instantly: useInstant,
          });
        }
        break;
      default:
        break;
    }
  }

  removeQueueItemsById(new Set(pending.map((item) => item.id)));
  replayed = true;
}

export async function initializePostHog(): Promise<PostHogJsClient | null> {
  const key = posthogKey();
  if (!key) return null;
  if (posthogClient && replayed) {
    if (queue.length > 0) {
      replayQueue(posthogClient);
    }
    return posthogClient;
  }
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const loaded = await importWithOneRetry(() => import('posthog-js'));
    if (!loaded) return null;
    const { default: posthog } = loaded;
    posthog.init(key, {
      api_host: posthogHost(),
      capture_pageview: false,
      mask_all_text: true,
      mask_all_element_attributes: true,
      disable_session_recording: isUnsubscribeRoutePathname(
        typeof window !== 'undefined' ? window.location.pathname : undefined,
      ),
      sanitize_properties: (properties) =>
        sanitizePosthogProperties(properties as Record<string, unknown>) ??
        {},
      before_send: (event) => {
        if (!event) return event;
        return sanitizePosthogEvent(
          event as { properties?: Record<string, unknown> },
        ) as typeof event;
      },
      loaded: (client) => {
        applyInternalTrafficFlag(window.location.search);
        const phClient = client as PostHogJsClient;
        queueMicrotask(() => {
          replayQueue(phClient);
          posthogClient = phClient;
          syncPostHogSessionRecordingForPathname(window.location.pathname);
          notifyReady(phClient);
        });
      },
    });
    return posthog as PostHogJsClient;
  })().catch(() => null);

  return initPromise;
}

/**
 * Sends queued captures in one batch when the tab is hidden or the page unloads before PostHog init.
 * Only removes items included in a successfully dispatched batch; later queue items replay on init.
 */
export function flushPreInitPostHogQueueOnPageLeave(
  transport?: Parameters<typeof sendPosthogBatch>[2],
): BeaconTransportResult | null {
  const key = posthogKey();
  if (!key || replayed) return null;
  if (isPosthogCapturingOptedOut(key)) {
    queue.length = 0;
    return null;
  }
  const pending = pendingQueueItems();
  if (pending.length === 0) return null;

  const payload = buildPosthogBatchPayload(key, toBeaconItems(pending));
  if (!payload) return null;

  const result = sendPosthogBatch(posthogHost(), payload, transport);
  lastBeaconResult = result;
  if (result.sent) {
    removeQueueItemsById(captureIdsIncludedInBeaconBatch(pending, payload));
  }
  return result;
}

/** Test-only reset */
export function resetAnalyticsClientForTests(): void {
  queue.length = 0;
  nextQueueId = 1;
  posthogClient = null;
  initPromise = null;
  replayed = false;
  lastBeaconResult = null;
  readyListeners.clear();
}

type QueueItemInput =
  | Omit<QueueCapture, 'id'>
  | Omit<QueueIdentify, 'id'>
  | Omit<QueueReset, 'id'>
  | Omit<QueueRegister, 'id'>;

function enqueue(item: QueueItemInput): void {
  const withId = { ...item, id: nextQueueId++ } as QueueItem;
  queue.push(withId);
  trimQueueIfNeeded();
}

function capture(event: string, properties?: Record<string, unknown>): void {
  if (!posthogKey()) return;
  const merged = sanitizePosthogProperties({
    ...captureTimeUrlProps(),
    ...(properties ?? {}),
  });
  if (posthogClient && replayed) {
    posthogClient.capture(event, merged);
    return;
  }
  enqueue({
    kind: 'capture',
    event,
    properties: merged,
    ts: Date.now(),
  });
}

function identify(distinctId: string, properties?: Record<string, unknown>): void {
  if (!posthogKey()) return;
  if (posthogClient && replayed) {
    posthogClient.identify(distinctId, properties);
    return;
  }
  enqueue({ kind: 'identify', distinctId, properties, ts: Date.now() });
}

function reset(): void {
  if (!posthogKey()) return;
  if (posthogClient && replayed) {
    posthogClient.reset();
    return;
  }
  enqueue({ kind: 'reset', ts: Date.now() });
}

function register(properties: Record<string, unknown>): void {
  if (!posthogKey()) return;
  if (posthogClient && replayed) {
    posthogClient.register(properties);
    return;
  }
  enqueue({ kind: 'register', properties, ts: Date.now() });
}

function getFeatureFlag(flag: string): string | boolean | undefined {
  if (!posthogClient || !replayed) return undefined;
  return posthogClient.getFeatureFlag(flag);
}

function onFeatureFlags(callback: () => void): () => void {
  let clientUnsub: (() => void) | undefined;
  const readyUnsub = subscribePostHogReady((client) => {
    if (!client) return;
    clientUnsub = client.onFeatureFlags(callback);
  });
  return () => {
    readyUnsub();
    clientUnsub?.();
  };
}

/** Single analytics facade — queues until PostHog lazy-inits, then forwards live. */
export const analyticsClient = {
  capture,
  identify,
  reset,
  register,
  getFeatureFlag,
  onFeatureFlags,
};

/** Back-compat alias for existing imports and test mocks. */
export const posthog = analyticsClient;

export function getAnalyticsQueueForTests(): readonly QueueItem[] {
  return queue;
}

export function isPostHogReplayComplete(): boolean {
  return replayed;
}

export function getPostHogClient(): PostHogJsClient | null {
  return posthogClient;
}

/** Toggle session replay when SPA navigates onto/off the unsubscribe route. */
export function syncPostHogSessionRecordingForPathname(pathname: string): void {
  if (!posthogClient || !replayed) return;
  posthogClient.set_config?.({
    disable_session_recording: isUnsubscribeRoutePathname(pathname),
  });
}

export function getLastBeaconResultForTests(): BeaconTransportResult | null {
  return lastBeaconResult;
}

/** @deprecated Beacon flushes are repeatable; use queue length / lastBeaconResult instead. */
export function wasEarlyExitBeaconFlushedForTests(): boolean {
  return lastBeaconResult?.sent === true;
}

export { posthogHost, posthogKey };
